import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/cn";

interface Ripple {
  id: string;
  x: number;
  y: number;
  color: string;
}

interface PresenceRippleProps {
  className?: string;
}

let rippleCounter = 0;

function PresenceRipple({ className }: PresenceRippleProps) {
  const [ripples, setRipples] = useState<Ripple[]>([]);

  const removeRipple = useCallback((id: string) => {
    setRipples((prev) => prev.filter((r) => r.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent<{ x: number; y: number; color: string }>) => {
      const id = `ripple-${++rippleCounter}`;
      setRipples((prev) => [...prev, { id, ...e.detail }]);
      setTimeout(() => removeRipple(id), 700);
    };

    window.addEventListener("presence-ripple" as string, handler as EventListener);
    return () => {
      window.removeEventListener("presence-ripple" as string, handler as EventListener);
    };
  }, [removeRipple]);

  return (
    <div className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}>
      <AnimatePresence>
        {ripples.map((ripple) => (
          <motion.div
            key={ripple.id}
            initial={{ scale: 0.3, opacity: 0.5 }}
            animate={{ scale: 1.5, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="absolute h-8 w-8 rounded-full"
            style={{
              left: ripple.x - 16,
              top: ripple.y - 16,
              backgroundColor: ripple.color,
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

PresenceRipple.displayName = "PresenceRipple";

export function emitPresenceRipple(x: number, y: number, color: string) {
  window.dispatchEvent(
    new CustomEvent("presence-ripple", { detail: { x, y, color } }),
  );
}

export { PresenceRipple };
export type { PresenceRippleProps };
